import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useMedia } from "@/context/MediaContext";
import { MediaFrame } from "@/components/media/MediaFrame";
import { Lightbox } from "@/components/media/Lightbox";
import { Eyebrow, Reveal } from "@/components/site/Reveal";

export const VideoGallery = ({ items, onOpen, cols = "lg:grid-cols-4", testid = "video-gallery" }) => (
  <>
    <div className={`hidden lg:grid ${cols} gap-5`} data-testid={testid}>
      {items.map((item, i) => (
        <Reveal key={item.id} delay={i * 0.06} y={40}>
          <figure className={i % 2 ? "lg:mt-14" : ""}>
            <MediaFrame item={item} onOpen={onOpen} className="rounded-sm" />
            <figcaption className="mt-4">
              {item.category && <p className="label-eyebrow text-[#38BDF8]">{item.category}</p>}
              {item.caption && <p className="mt-1.5 text-[#2E1B16]/70 text-sm leading-relaxed max-w-[26ch]">{item.caption}</p>}
            </figcaption>
          </figure>
        </Reveal>
      ))}
    </div>
    <div className="lg:hidden -mx-6 px-6 flex gap-4 overflow-x-auto snap-x snap-mandatory no-scrollbar pb-2" data-testid={`${testid}-mobile`}>
      {items.map((item) => (
        <figure key={item.id} className="snap-center shrink-0 w-[72vw] sm:w-[44vw]">
          <MediaFrame item={item} onOpen={onOpen} autoplay hoverPlay={false} className="rounded-sm" />
          <figcaption className="mt-3">
            {item.category && <p className="label-eyebrow text-[#38BDF8]">{item.category}</p>}
            {item.caption && <p className="mt-1 text-[#2E1B16]/70 text-sm leading-relaxed">{item.caption}</p>}
          </figcaption>
        </figure>
      ))}
    </div>
  </>
);

const Showcase = () => {
  const { items, hasReal } = useMedia("showcase", 0);
  const [open, setOpen] = useState(null);
  if (!hasReal) return null;
  return (
    <section id="work" className="py-28 md:py-44 bg-[#F2EFE9]" data-testid="showcase" aria-labelledby="showcase-heading">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid lg:grid-cols-[1fr_auto] gap-8 items-end mb-14 md:mb-20">
          <div>
            <Reveal><Eyebrow>The work</Eyebrow></Reveal>
            <Reveal delay={0.05}>
              <h2 id="showcase-heading" className="mt-7 font-display uppercase text-4xl sm:text-5xl lg:text-7xl tracking-tight leading-[0.95] text-[#2E1B16]">
                This is what that looks like.
              </h2>
            </Reveal>
          </div>
          <Reveal delay={0.1}>
            <p className="text-lg text-[#2E1B16]/60 max-w-sm leading-relaxed lg:text-right">
              Real businesses. Real people. Real processes. Turned into content people actually want to watch.
            </p>
          </Reveal>
        </div>

        <VideoGallery items={items.slice(0, 4)} onOpen={setOpen} />

        <Reveal delay={0.1}>
          <div className="mt-14 md:mt-20 flex justify-end">
            <Link to="/work" data-testid="showcase-more" className="group inline-flex items-center gap-3 font-display text-xl md:text-2xl text-[#2E1B16] hover:text-[#38BDF8] transition-colors">
              View more work
              <ArrowRight size={22} className="transition-transform duration-300 group-hover:translate-x-1.5" />
            </Link>
          </div>
        </Reveal>
      </div>
      <Lightbox item={open} onClose={() => setOpen(null)} />
    </section>
  );
};

export default Showcase;
